import React, { useState } from "react";
import Avatar from "./Avatar";
import IconPicker, { iconsToList } from "../../admin/IconPicker";
import { apiRequest, errorMessage } from "../../api";
import { SERVICE_ICONS } from "../../data/serviceIcons";
import { cardClass } from "./jobUtils";

const inputClass = "w-full px-4 py-2.5 rounded-lg bg-white/5 border border-navy-border text-white focus:outline-none focus:border-accent-light";
const widePickerClass = "grid grid-cols-2 sm:grid-cols-3 gap-1 max-h-64 overflow-y-auto p-1.5 bg-white/5 border border-navy-border rounded-md";

// Edit the signed-in member's profile: photo, contact details and about text. Sellers also pick the
// services they offer (these power the "Find Seller" filters), their skills and hourly rate.
export default function ProfileForm({ user, onSaved }) {
  const isSeller = user.role === "seller";
  const [form, setForm] = useState({
    full_name: user.full_name || "", phone: user.phone || "", country: user.country || "", company: user.company || "",
    headline: user.headline || "", skills: user.skills || "", services: user.services || "",
    hourly_rate: user.hourly_rate ?? "", bio: user.bio || "",
  });
  const [photo, setPhoto] = useState(null);
  const [removePhoto, setRemovePhoto] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const pickPhoto = (e) => {
    const file = e.target.files[0];
    if (file && file.size > 3 * 1024 * 1024) {
      e.target.value = "";
      return setError("The photo must be 3 MB or smaller.");
    }
    setError("");
    setPhoto(file || null);
    setRemovePhoto(false);
  };

  const toggleService = (key) => {
    const current = iconsToList(form.services);
    setForm({ ...form, services: (current.includes(key) ? current.filter((k) => k !== key) : [...current, key]).join(",") });
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMessage("");
    setError("");
    const fd = new FormData();
    Object.entries(form).forEach(([key, value]) => fd.append(key, value ?? ""));
    if (photo) fd.append("avatar", photo);
    else if (removePhoto) fd.append("avatar", "");   // an empty value removes the saved photo
    const { ok, data } = await apiRequest("/core/cowork/me/", { method: "PATCH", auth: true, body: fd });
    setBusy(false);
    if (!ok) return setError(errorMessage(data));
    setPhoto(null);
    setRemovePhoto(false);
    setMessage("Profile saved.");
    onSaved(data);
  };

  const preview = photo ? URL.createObjectURL(photo) : removePhoto ? null : user.avatar;

  return (
    <form onSubmit={save} className={`${cardClass} p-6 max-w-3xl space-y-5`}>
      <div className="flex flex-wrap items-center gap-5">
        <Avatar src={preview} name={form.full_name || user.email} size={96} />
        <div className="space-y-2">
          <p className="text-sm text-gray-300">Profile photo</p>
          <input type="file" accept="image/*" onChange={pickPhoto} className="text-sm text-gray-300" />
          {(user.avatar || photo) && !removePhoto && (
            <button type="button" onClick={() => { setPhoto(null); setRemovePhoto(true); }} className="block text-xs text-red-400 hover:underline">Remove photo</button>
          )}
          <p className="text-xs text-gray-500">A clear, square photo works best. Max 3 MB.</p>
        </div>
      </div>

      <p className="text-sm text-gray-400">Email: <span className="text-white">{user.email}</span></p>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-gray-300 mb-1">Full name</label>
          <input required className={inputClass} value={form.full_name} onChange={set("full_name")} />
        </div>
        <div>
          <label className="block text-sm text-gray-300 mb-1">Phone</label>
          <input className={inputClass} value={form.phone} onChange={set("phone")} />
        </div>
        <div>
          <label className="block text-sm text-gray-300 mb-1">Country</label>
          <input className={inputClass} value={form.country} onChange={set("country")} />
        </div>
        {isSeller ? (
          <div>
            <label className="block text-sm text-gray-300 mb-1">Hourly rate</label>
            <input type="number" min="0" step="0.01" className={inputClass} value={form.hourly_rate} onChange={set("hourly_rate")} />
          </div>
        ) : (
          <div>
            <label className="block text-sm text-gray-300 mb-1">Company / organisation</label>
            <input className={inputClass} value={form.company} onChange={set("company")} />
          </div>
        )}
      </div>

      {isSeller && (
        <>
          <div>
            <label className="block text-sm text-gray-300 mb-1">Headline</label>
            <input className={inputClass} value={form.headline} onChange={set("headline")} placeholder="e.g. Logo & brand designer" />
          </div>
          <div>
            <label className="block text-sm text-gray-300 mb-2">Services you offer <span className="text-gray-500">(buyers can filter sellers by these)</span></label>
            <IconPicker className={widePickerClass} icons={SERVICE_ICONS} selected={iconsToList(form.services)} onToggle={toggleService} />
          </div>
          <div>
            <label className="block text-sm text-gray-300 mb-1">Skills (comma separated)</label>
            <input className={inputClass} value={form.skills} onChange={set("skills")} placeholder="Illustrator, Photoshop, Branding" />
          </div>
        </>
      )}

      <div>
        <label className="block text-sm text-gray-300 mb-1">About you</label>
        <textarea rows={4} className={inputClass} value={form.bio} onChange={set("bio")} />
      </div>

      {message && <p className="text-sm text-green-400">{message}</p>}
      {error && <p className="text-sm text-red-400">{error}</p>}
      <button disabled={busy} className="px-6 py-2.5 rounded-full bg-brand-gradient font-semibold disabled:opacity-60">
        {busy ? "Saving..." : "Save Profile"}
      </button>
    </form>
  );
}
